import { ListKeyManager } from '@angular/cdk/a11y';
import {
  ConnectedPosition,
  Overlay,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  addRegionFilter,
  availableRadiusOptions,
  RegionSearchService,
  resetRegionFilter,
  selectCountries,
  selectInitialSearchTerm,
  selectRegionFilters,
} from '@immosky/object-search-filter';
import {
  AppState,
  Country,
  FilterOption,
  RadiusFilterOption,
  Region,
  RegionSearchResult,
} from '@immosky/types';
import { select, Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  switchMap,
  tap,
} from 'rxjs/operators';
import { DetailPageService } from '../../services/detail-page.service';
import { SearchInputComponent } from '../search-input/search-input.component';
import { SearchBoxResultFinder } from './search-box-result-finder';

@Component({
  selector: 'lib-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBoxComponent implements OnInit, AfterViewInit {
  private readonly MIN_SEARCH_LENGTH = 1;

  activeRegions: Region[] = [];
  options: FilterOption<RegionSearchResult | number>[] = [];

  searchControl = new FormControl('');
  resultFinder!: SearchBoxResultFinder;

  isDropdownOpen = false;
  hasNoResults = false;

  private listKeyManager!: ListKeyManager<HTMLButtonElement>;
  activeResultIndex = 0;

  private justFocused = false;

  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      overlayX: 'start',
      originY: 'bottom',
      overlayY: 'top',
    },
  ];

  @Input()
  placeholderText = 'filters.search.label';

  @Input()
  disableReset = false;

  @Input()
  isMapVisible = false;

  @Input()
  withChips = true;

  @Input()
  radiusOptions: RadiusFilterOption[] = availableRadiusOptions;

  @Input()
  forceHideRadius = false;

  @ViewChildren('resultButton')
  resultButtons!: QueryList<HTMLButtonElement>;

  @ViewChild('searchInput')
  searchInput!: SearchInputComponent;

  @Output()
  openMobileFilter = new EventEmitter();

  @Output()
  inputFocus = new EventEmitter();

  @Output()
  selectRegion = new EventEmitter();

  get scrollStrategy(): ScrollStrategy {
    return this.overlay.scrollStrategies.block();
  }

  get hasActiveRegion(): boolean {
    return this.activeRegions.length > 0;
  }

  get hasMultipleActiveRegions(): boolean {
    return this.activeRegions.length > 1;
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>,
    private regionSearchService: RegionSearchService,
    private detailPageService: DetailPageService,
    private overlay: Overlay
  ) {
    this.store.select(selectCountries).subscribe((countries: Country[]) => {
      this.resultFinder = new SearchBoxResultFinder(
        this.regionSearchService,
        countries[0]
      );
    });
  }

  ngOnInit(): void {
    this.store
      .pipe(select(selectRegionFilters))
      .subscribe((regions: Region[]) => {
        this.activeRegions = regions;
        this.cdRef.markForCheck();
      });

    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(150),
        tap((searchTerm: string) => {
          if (!searchTerm || searchTerm.length < this.MIN_SEARCH_LENGTH) {
            this.clearResults();
            this.isDropdownOpen = false;
            this.cdRef.markForCheck();
          }
        }),
        filter((searchTerm: string) => {
          return !!searchTerm && searchTerm.length >= this.MIN_SEARCH_LENGTH;
        }),
        switchMap((searchTerm: string) => {
          return this.resultFinder.search(searchTerm);
        })
      )
      .subscribe((options: FilterOption<RegionSearchResult | number>[]) => {
        this.options = options;
        this.hasNoResults = this.options.length === 0;
        this.isDropdownOpen = true;
        this.listKeyManager.setFirstItemActive();
        this.cdRef.markForCheck();
      });

    this.updateInitialSearchTerm();
  }

  private updateInitialSearchTerm() {
    let initialSearchTerm = '';

    this.store
      .pipe(
        select(selectInitialSearchTerm),
        first(),
        filter((searchTerm: string) => !!searchTerm),
        tap((searchTerm: string) => {
          initialSearchTerm = searchTerm;
        }),
        switchMap((searchTerm: string) => {
          return this.resultFinder.search(searchTerm);
        })
      )
      .subscribe((options: FilterOption<RegionSearchResult | number>[]) => {
        if (options.length === 1) {
          this.select(options[0]);
        } else {
          this.searchControl.setValue(initialSearchTerm, {
            emitEvent: false,
          });

          this.options = options;
          this.hasNoResults = this.options.length === 0;
          this.isDropdownOpen = true;

          this.cdRef.markForCheck();
        }
      });
  }

  ngAfterViewInit(): void {
    this.listKeyManager = new ListKeyManager(this.resultButtons)
      .withVerticalOrientation()
      .withHomeAndEnd()
      .withWrap();

    this.listKeyManager.change.subscribe((index: number) => {
      this.activeResultIndex = index;
      this.cdRef.markForCheck();
    });

    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (this.isDropdownOpen) {
        this.listKeyManager.onKeydown(event);
      }
    });
  }

  onOpenMobileFilter() {
    this.openMobileFilter.emit();
  }

  select(option: FilterOption<RegionSearchResult | number>) {
    if (typeof option.value === 'number') {
      location.href = this.detailPageService.getDetailPageUrlForId(
        `${option.value}`
      );
    } else {
      this.regionSearchService
        .searchByID([option.value.id])
        .pipe(first())
        .subscribe((region) => {
          this.store.dispatch(
            addRegionFilter({
              region: region[0],
            })
          );

          this.clearSearchTerm();
          this.clearResults();
          this.close();
          this.selectRegion.emit();
        });
    }
  }

  getPlaceType(
    option: FilterOption<RegionSearchResult | number>
  ): string | null {
    if ((option.value as RegionSearchResult).placeType) {
      return `regionSearch.${(option.value as RegionSearchResult).placeType}`;
    }
    return null;
  }

  private clearSearchTerm() {
    this.searchControl.setValue('', {
      emitEvent: false,
    });
  }

  onEnter() {
    const option = this.options[this.activeResultIndex];
    this.select(option);
  }

  onEscape() {
    this.close();
  }

  close() {
    if (!this.justFocused) {
      this.isDropdownOpen = false;
    }
  }

  focusResult(index: number) {
    this.listKeyManager.setActiveItem(index);
  }

  clearResults() {
    this.options = [];
    this.hasNoResults = false;
  }

  trackBy(index: number, option: FilterOption) {
    return option.value;
  }

  onInputFocus() {
    this.inputFocus.emit();

    if (this.options.length > 0) {
      this.isDropdownOpen = true;
      this.hasNoResults = false;
      this.justFocused = true;

      setTimeout(() => {
        this.justFocused = false;
      }, 100);
    }
  }

  focusInput() {
    this.searchInput?.focus();
  }

  clearInput() {
    this.searchControl.setValue('');
  }

  removeAllRegions() {
    this.store.dispatch(resetRegionFilter());
    this.searchControl.setValue('', { emitEvent: false });
    this.isDropdownOpen = false;
    this.hasNoResults = true;
    this.options = [];
  }
}
